import React from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
}) => {
  // Portaled to <body>: `position: fixed` breaks inside transformed ancestors
  // (framer-motion containers, editor overlays) — the dialog and its
  // click-catching backdrop would render trapped and invisible there.
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: EASE_OUT }}
      className="fixed inset-0 z-50 overflow-y-auto"
      // Marks the portal root so dismiss-on-outside-click owners (e.g. Teri's
      // history dropdown) can tell "the member clicked away" from "the member
      // clicked the confirmation I opened", and not unmount it mid-click.
      data-confirm-dialog="true"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-grey-500 bg-opacity-75 dark:bg-grey-900 dark:bg-opacity-75"
          onClick={onCancel}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: EASE_OUT }}
          style={{ willChange: 'transform, opacity' }}
          className="inline-block align-bottom bg-surface-primary rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
        >
          <div className="sm:flex sm:items-start">
            <div
              className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                confirmVariant === 'danger'
                  ? 'bg-error-100 dark:bg-error-900/20'
                  : 'bg-primary-100 dark:bg-primary-900/20'
              }`}
            >
              {confirmVariant === 'danger' ? (
                <svg
                  className="h-6 w-6 text-error-600 dark:text-error-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              ) : (
                <svg
                  className="h-6 w-6 text-primary-600 dark:text-primary-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-text-primary">
                {title}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-text-secondary">{message}</p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
            <button
              type="button"
              onClick={onConfirm}
              className={`w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 sm:w-auto sm:text-sm ${ confirmVariant === 'danger' ? 'bg-error-600 hover:bg-error-700 focus-visible:ring-error-500' : 'bg-primary-600 dark:bg-primary-350 hover:bg-primary-700 dark:hover:bg-primary-400 focus-visible:ring-primary-500' }`}
            >
              {confirmLabel}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-elevated text-base font-medium text-text-primary hover:bg-grey-50 dark:hover:bg-grey-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 focus-visible:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              {cancelLabel}
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>,
    document.body
  );
};

export default ConfirmDialog;
